/**
 * Extração local (sem IA, sem Maps, sem créditos) do DESTINO de um pedido.
 *
 * Regras gerais (não é uma lista de cidades):
 *  - construções naturais: "quero ir ao Porto", "vou para o Porto",
 *    "quero visitar Porto", "quero conhecer o Porto", "estou de visita ao
 *    Porto", "passar seis horas no Porto", "explorar Porto", equivalentes sem
 *    acentos e em EN/ES/FR/IT/DE;
 *  - artigos e preposições (ao, à, no, na, para, em, del, zum…) nunca fazem
 *    parte do nome da cidade;
 *  - cidades compostas são preservadas ("Vila Nova de Gaia", "San Sebastián");
 *  - origem e destino são distinguidos: "vou do Porto para Braga" → Braga.
 */

import { extractExplicitLocality } from "./geo-identity";


const fold = (value: string) =>
  value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/gu, "")
    .toLowerCase();

const clean = (value: string) =>
  value
    .replace(/[\s,;:.!?"'“”()\-–—]+$/u, "")
    .replace(/^\s+/u, "")
    .replace(/\s+/gu, " ")
    .trim();

/** Preposições/artigos que podem preceder o nome e nunca lhe pertencem. */
const LEADING_PARTICLES = [
  "ao",
  "aos",
  "a",
  "as",
  "à",
  "às",
  "no",
  "na",
  "nos",
  "nas",
  "em",
  "para",
  "pra",
  "pro",
  "de",
  "do",
  "da",
  "dos",
  "das",
  "o",
  "os",
  "um",
  "uma",
  "to",
  "in",
  "at",
  "the",
  "into",
  "al",
  "a la",
  "los",
  "las",
  "el",
  "la",
  "hacia",
  "au",
  "aux",
  "aux",
  "vers",
  "le",
  "les",
  "l",
  "du",
  "des",
  "nel",
  "nella",
  "verso",
  "il",
  "lo",
  "gli",
  "i",
  "nach",
  "zum",
  "zur",
  "der",
  "die",
  "das",
  "den",
  "dem",
  "ins",
];

/** Palavras que terminam o nome de uma cidade quando aparecem a seguir. */
const TRAILING_STOPWORDS = new Set([
  "e",
  "o",
  "a",
  "the",
  "and",
  "y",
  "et",
  "und",
  "ed",
  "durante",
  "por",
  "para",
  "pra",
  "com",
  "sem",
  "onde",
  "quando",
  "que",
  "tenho",
  "terei",
  "vou",
  "vamos",
  "quero",
  "queria",
  "gostava",
  "pretendo",
  "estou",
  "fico",
  "ficar",
  "passar",
  "visitar",
  "conhecer",
  "explorar",
  "ver",
  "for",
  "with",
  "without",
  "where",
  "when",
  "have",
  "want",
  "during",
  "about",
  "por",
  "con",
  "sin",
  "quiero",
  "tengo",
  "pendant",
  "avec",
  "sans",
  "je",
  "veux",
  "per",
  "con",
  "voglio",
  "ho",
  "fur",
  "mit",
  "ohne",
  "ich",
  "will",
  "habe",
  "moechte",
  "perto",
  "cerca",
  "junto",
  "proximo",
  "proxima",
  "beira",
  "volta",
  "near",
  "around",
  "das",
  "dos",
  "pelas",
  "pelos",
  "sobre",
  "depois",
  "antes",
  "ate",
  "horas",
  "hora",
  "hours",
  "hour",
  "dias",
  "dia",
  "days",
  "day",
  "noites",
  "nights",
  "giorni",
  "giorno",
  "jours",
  "jour",
  "tage",
  "tag",
  "stunden",
  "ore",
  // Frases de intenção/pergunta que nunca fazem parte do nome do destino
  // ("Veneza diz me o que posso ver", "Porto, diz-me…", "Paris. Quantos dias?").
  "diz",
  "dizme",
  "dizem",
  "digam",
  "dime",
  "dimelo",
  "dimmi",
  "dismoi",
  "dis",
  "sag",
  "sagmir",
  "me",
  "moi",
  "mir",
  "posso",
  "podes",
  "pode",
  "podemos",
  "recomenda",
  "recomendas",
  "recomendam",
  "recomiendas",
  "recommande",
  "recommend",
  "recommends",
  "consigli",
  "consiglia",
  "empfiehlst",
  "sugere",
  "sugeres",
  "sugestoes",
  "suggest",
  "suggests",
  "sabes",
  "saber",
  "quantos",
  "quantas",
  "quanto",
  "quanta",
  "quanti",
  "quante",
  "cuantos",
  "cuantas",
  "combien",
  "wieviele",
  "wie",
  "many",
  "much",
  "how",
  "what",
  "which",
  "should",
  "can",
  "could",
  "tell",
  "show",
  "qual",
  "quais",
  "cual",
  "cuales",
  "quel",
  "quels",
  "quale",
  "quali",
  "welche",
  "was",
]);


/** Nunca são cidades. */
const NOT_A_CITY = new Set([
  "casa",
  "trabalho",
  "hotel",
  "hoteis",
  "aeroporto",
  "estacao",
  "centro",
  "gastronomia",
  "cultura",
  "natureza",
  "comida",
  "museus",
  "restaurantes",
  "ferias",
  "viagem",
  "viajar",
  "visita",
  "passagem",
  "escapadinha",
  "cidade",
  "cidades",
  "city",
  "town",
  "home",
  "work",
  "airport",
  "station",
  "there",
  "here",
  "la",
  "ali",
  "aqui",
  "manha",
  "tarde",
  "noite",
  "semana",
  "fim",
  "hoje",
  "amanha",
  "food",
  "culture",
  "nature",
  "vacation",
  "trip",
  "beira",
  "sitio",
  "lugar",
  "comboio",
  "aviao",
  "autocarro",
  "carro",
  "metro",
  "train",
  "plane",
  "bus",
  "car",
]);

/** Um candidato com uma destas palavras não é o nome de uma cidade. */
const DISQUALIFYING_TOKENS = new Set([
  // Frases genéricas de intenção turística nunca são o nome de uma cidade
  // ("quero visitar alguns pontos turísticos").
  "ponto",
  "pontos",
  "turistico",
  "turisticos",
  "turistica",
  "turisticas",
  "atracao",
  "atracoes",
  "atraccion",
  "atracciones",
  "sights",
  "sight",
  "attraction",
  "attractions",
  "landmark",
  "landmarks",
  "lugares",
  "sitios",
  "alguns",
  "algumas",
  "algunos",
  "algunas",
  "varios",
  "varias",
  "some",
  "several",

  "aeroporto",
  "aeropuerto",
  "aeroport",
  "flughafen",
  "airport",
  "estacao",
  "station",
  "gare",
  "bahnhof",
  "hotel",
  "centro",
  "beira",
  "comboio",
  "aviao",
  "autocarro",
  "carro",
  "metro",
  "train",
  "plane",
  "bus",
]);

const NAME = "[\\p{L}][\\p{L}\\p{N}'’.\\-]*(?:[ \\-][\\p{L}][\\p{L}\\p{N}'’.\\-]*){0,4}";

/**
 * Verbos de deslocação usados sem preposição ("Vou Pequim 3 dias"). Muito
 * frequente em PT/ES/IT falado, e também em EN/FR/DE informal.
 */
const MOTION_VERBS =
  "(?:vou|vamos|irei|iremos|estou|estarei|fico|ficarei|voy|estoy|vado|andiamo|sto|je\\s+vais|on\\s+va|nous\\s+allons|i'?m\\s+going|i\\s+am\\s+going|we'?re\\s+going|we\\s+are\\s+going|ich\\s+fahre|wir\\s+fahren)";

/**
 * Palavras que nunca podem iniciar o destino logo a seguir ao verbo:
 * preposições/artigos (tratados pelos padrões com preposição) e infinitivos
 * ou advérbios ("vou comer francesinha", "vou embora").
 */
const NO_PREPOSITION_BLOCKERS =
  "(?:de|do|da|dos|das|desde|del|della|von|vom|depuis|com|sem|a|ao|aos|à|às|para|pro|pra|até|ate|em|no|na|nos|nas|por|to|in|at|the|en|el|la|le|les|nach|zum|zur|dans|au|aux|ir|viajar|ver|visitar|conhecer|explorar|descobrir|comer|jantar|almo[cç]ar|beber|provar|comprar|dormir|ficar|passar|passear|sair|estar|andar|fazer|apanhar|levar|trazer|alugar|tentar|procurar|pedir|reservar|tomar|come[cç]ar|acabar|voltar|regressar|chegar|partir|embora|hoje|amanh[ãa]|logo|go|travel|visit|see|eat|stay|essen|sehen|bleiben)";

/**
 * O nome só é aceite quando termina a frase ou é seguido de duração/tempo —
 * assim "Vou Pequim 3 dias" é destino e "Vou levar o carro" não é.
 */
const NO_PREPOSITION_TAIL =
  "(?=\\s*(?:[,;:.!?]|$)|\\s+\\d{1,2}(?:[.,]\\d+)?\\s*(?:h|horas?|hours?|dias?|days?|jours?|giorni|tage|noites?|nights?)\\b|\\s+(?:amanh[ãa]|hoje|manh[ãa]|tomorrow|today|next|pr[oó]xim[ao])\\b)";

/** Verbos/expressões que introduzem um destino, seguidos de preposição opcional. */
const DESTINATION_PATTERNS: RegExp[] = [
  // Sem preposição: "Vou Pequim 3 dias", "Voy Milán 2 días", "Vado Roma 2 giorni".
  new RegExp(
    `\\b${MOTION_VERBS}\\s+(?!${NO_PREPOSITION_BLOCKERS}\\b)(${NAME})${NO_PREPOSITION_TAIL}`,
    "iu",
  ),
  // PT
  // Forma coloquial frequente sem preposição: "quero ir Atenas 2 dias".
  // A duração/palavras de continuação terminam o nome em normalizeCityName,
  // mantendo a regra genérica para qualquer cidade.
  new RegExp(
    `\\b(?:quero|queria|gostava\\s+de|gostaria\\s+de|pretendo|vou|vamos|irei|iremos|ia|penso)\\s+(?:de\\s+)?(?:ir|viajar)\\s+(?!(?:de|com|sem)\\b)(${NAME})`,
    "iu",
  ),
  new RegExp(
    `\\b(?:quero|queria|gostava\\s+de|gostaria\\s+de|pretendo|vou|vamos|irei|iremos|ia|penso)\\s+(?:de\\s+)?(?:ir|viajar|deslocar-me)\\s+(?:a|ao|aos|à|às|para|pro|pra|até|ate)\\s+(?:o\\s+|a\\s+|os\\s+|as\\s+)?(${NAME})`,
    "iu",
  ),
  new RegExp(
    `\\b(?:quero|queria|gostava\\s+de|gostaria\\s+de|pretendo|vou|vamos|irei|iremos)\\s+(?:visitar|conhecer|explorar|descobrir|passear\\s+por)\\s+(?:o\\s+|a\\s+|os\\s+|as\\s+)?(${NAME})`,
    "iu",
  ),
  new RegExp(
    `\\b(?:estou|estarei|vou\\s+estar|ando)\\s+(?:de\\s+)?(?:visita|passagem|viagem)\\s+(?:a|ao|à|em|no|na|por)\\s+(?:o\\s+|a\\s+)?(${NAME})`,
    "iu",
  ),
  new RegExp(
    `\\b(?:passar|ficar|estar)\\s+(?:[\\p{L}\\p{N}.,]+\\s+){0,4}?(?:em|no|na|nos|nas)\\s+(?:o\\s+|a\\s+)?(${NAME})`,
    "iu",
  ),
  new RegExp(
    `\\b(?:viagem|ida|escapadinha|fim\\s+de\\s+semana|roteiro|rota|itinerário|itinerario)\\s+(?:a|ao|à|para|em|no|na)\\s+(?:o\\s+|a\\s+)?(${NAME})`,
    "iu",
  ),
  new RegExp(
    `\\b(?:vou|vamos|irei|iremos|viajo|viajamos)\\s+(?:a|ao|aos|à|às|para|pro|pra)\\s+(?:o\\s+|a\\s+|os\\s+|as\\s+)?(${NAME})`,
    "iu",
  ),
  // "quero ver Atenas em 4 horas": a duração torna o nome anterior num
  // destino, sem confundir pedidos genéricos como "quero ver museus".
  new RegExp(
    `\\b(?:quero|queria|gostava\\s+de|gostaria\\s+de|pretendo)\\s+(?:ver|visitar|conhecer|explorar)\\s+(?:o\\s+|a\\s+)?(${NAME})(?=\\s+(?:em|durante|por)\\s+\\d{1,2}(?:[.,]\\d+)?\\s*(?:horas?|dias?)\\b)`,
    "iu",
  ),
  // Pergunta natural: "o que posso ver/fazer em Atenas durante 2 dias?".
  // A duração funciona como limite do nome e evita capturar o resto da frase.
  new RegExp(
    `\\b(?:o\\s+que\\s+)?(?:posso|podemos|d[aá]\\s+para)\\s+(?:ver|fazer|visitar|conhecer|explorar)\\s+(?:em|no|na|por)\\s+(?:o\\s+|a\\s+)?(${NAME})(?=\\s+(?:em|durante|por)\\s+\\d{1,2}(?:[.,]\\d+)?\\s*(?:horas?|dias?)\\b|[?!.,]|$)`,
    "iu",
  ),
  new RegExp(`\\bexplorar\\s+(?:o\\s+|a\\s+)?(${NAME})`, "iu"),
  // "quero ver museus em Londres", "comer francesinha no Porto": a categoria
  // pedida fica antes da preposição e o destino é a cidade que vem a seguir.
  new RegExp(
    `\\b(?:ver|visitar|conhecer|comer|jantar|almo[cç]ar|provar|comprar|encontrar|beber|dormir|ficar)\\s+(?:[\\p{L}\\p{M}'’-]+\\s+){0,3}?(?:em|no|na|nos|nas|in|at)\\s+(?:o\\s+|a\\s+|the\\s+)?(${NAME})`,
    "iu",
  ),

  // EN
  new RegExp(
    `\\b(?:i\\s+(?:want|would\\s+like|plan|am\\s+going)|we\\s+(?:want|are\\s+going)|going|travelling|traveling)\\s+(?:to\\s+)?(?:go\\s+to|travel\\s+to|visit|explore|discover|to)\\s+(?:the\\s+)?(${NAME})`,
    "iu",
  ),
  new RegExp(
    `\\b(?:trip|visit|weekend|itinerary|route)\\s+(?:to|in)\\s+(?:the\\s+)?(${NAME})`,
    "iu",
  ),
  new RegExp(
    `\\b(?:spend|spending|stay|staying)\\s+(?:[\\p{L}\\p{N}.,]+\\s+){0,4}?(?:in|at)\\s+(?:the\\s+)?(${NAME})`,
    "iu",
  ),
  // ES
  new RegExp(
    `\\b(?:quiero|queremos|voy|vamos|pienso)\\s+(?:a\\s+)?(?:ir\\s+a|viajar\\s+a|visitar|conocer|explorar|a)\\s+(?:el\\s+|la\\s+)?(${NAME})`,
    "iu",
  ),
  new RegExp(
    `\\b(?:pasar|estar)\\s+(?:[\\p{L}\\p{N}.,]+\\s+){0,4}?en\\s+(?:el\\s+|la\\s+)?(${NAME})`,
    "iu",
  ),
  // FR
  new RegExp(
    `\\b(?:je\\s+veux|je\\s+voudrais|nous\\s+allons|je\\s+vais|on\\s+va)\\s+(?:aller\\s+[àa]|visiter|découvrir|decouvrir|explorer|[àa])\\s+(?:le\\s+|la\\s+)?(${NAME})`,
    "iu",
  ),
  new RegExp(`\\b(?:passer|rester)\\s+(?:[\\p{L}\\p{N}.,]+\\s+){0,4}?[àa]\\s+(${NAME})`, "iu"),
  // IT
  new RegExp(
    `\\b(?:voglio|vorrei|vado|andiamo)\\s+(?:andare\\s+a|visitare|scoprire|esplorare|a|in)\\s+(?:il\\s+|la\\s+)?(${NAME})`,
    "iu",
  ),
  new RegExp(
    `\\b(?:passare|restare)\\s+(?:[\\p{L}\\p{N}.,]+\\s+){0,4}?(?:a|in)\\s+(${NAME})`,
    "iu",
  ),
  // DE
  new RegExp(
    `\\b(?:ich\\s+(?:will|möchte|mochte|fahre|reise)|wir\\s+(?:wollen|fahren|reisen))\\s+(?:nach|zum|zur|in|besuchen)?\\s*(${NAME})`,
    "iu",
  ),
  new RegExp(`\\b(?:verbringen|bleiben)\\s+(?:[\\p{L}\\p{N}.,]+\\s+){0,4}?in\\s+(${NAME})`, "iu"),
  // Duração + preposição: "2 dias em Lisboa", "3 days in Rome", "2 giorni a Roma".
  new RegExp(
    `\\b\\d{1,2}\\s*(?:dias?|days?|d[ií]as?|jours?|giorni|tage|horas?|hours?|heures|ore|stunden)\\s+(?:em|no|na|nos|nas|in|at|a|ao|à|en|à|a|nach)\\s+(?:o\\s+|a\\s+|the\\s+|el\\s+|la\\s+|le\\s+)?(${NAME})`,
    "iu",
  ),
];

/** "vou do Porto para Braga" → origem Porto, destino Braga. */
const ORIGIN_DESTINATION = new RegExp(
  `\\b(?:de|do|da|desde|from|von|da|depuis)\\s+(${NAME})\\s+(?:para|até|ate|a|ao|à|to|nach|hacia|vers|verso|fino\\s+a)\\s+(?:o\\s+|a\\s+|the\\s+)?(${NAME})`,
  "iu",
);

function stripLeadingParticles(value: string): string {
  let out = clean(value);
  let changed = true;
  while (changed) {
    changed = false;
    for (const particle of LEADING_PARTICLES) {
      const re = new RegExp(`^${particle.replace(/ /g, "\\s+")}\\s+`, "iu");
      if (re.test(out)) {
        out = clean(out.replace(re, ""));
        changed = true;
        break;
      }
    }
  }
  return out;
}

/** Corta o candidato na primeira palavra que não pode pertencer ao nome. */
function trimAtStopword(value: string): string {
  const words = value.split(" ");
  const kept: string[] = [];
  for (const word of words) {
    const key = fold(word.replace(/[^\p{L}\p{N}]/gu, ""));
    if (!key) break;
    if (kept.length && TRAILING_STOPWORDS.has(key)) break;
    if (!kept.length && TRAILING_STOPWORDS.has(key)) return "";
    if (/^\d+$/.test(key)) break;
    kept.push(word);
    if (kept.length >= 5) break;
  }
  return kept.join(" ");
}

/** Normaliza "ao Porto", "no Porto", "the Porto" → "Porto". */
export function normalizeCityName(raw: string): string | undefined {
  const trimmed = trimAtStopword(stripLeadingParticles(raw ?? ""));
  const value = clean(trimmed);
  if (!value || value.length < 2) return undefined;
  const key = fold(value);
  if (NOT_A_CITY.has(key) || TRAILING_STOPWORDS.has(key)) return undefined;
  if (value.split(" ").some((word) => DISQUALIFYING_TOKENS.has(fold(word)))) return undefined;
  // "Porto" em minúsculas continua a ser Porto; mas quando o utilizador já
  // escreveu o nome com maiúsculas preservamos exactamente ("Rothenburg ob
  // der Tauber", "Vila Nova de Gaia").
  if (/\p{Lu}/u.test(value)) return value;
  return value
    .split(" ")
    .map((word, index) => {
      const lower = word.toLocaleLowerCase();
      const minor = ["de", "do", "da", "dos", "das", "del", "di", "du", "van", "of", "the", "e"];
      if (index > 0 && minor.includes(fold(lower))) return lower;
      return word[0].toLocaleUpperCase() + word.slice(1);
    })
    .join(" ");
}

export type ExtractedDestination = {
  /** Destino explícito encontrado no texto (já normalizado). */
  city?: string;
  /** Origem explícita, quando o pedido a distingue do destino. */
  origin?: string;
  /** True quando o texto contém mesmo um destino — não perguntar a cidade. */
  explicit: boolean;
};

/**
 * Pedidos de recomendação ("melhor doce tradicional de Viana do Castelo",
 * "onde posso comprar doces regionais em Viana do Castelo?"): o destino vem
 * depois de uma preposição, e o produto pedido nunca pode ser confundido com
 * a cidade.
 */
const DISCOVERY_LEAD = new RegExp(
  `\\b(?:melhor(?:es)?|mejor(?:es)?|migliori?|meilleur\\w*|beste[nrs]?|best|top\\s*\\d*|onde\\s+(?:posso\\s+|se\\s+pode\\s+)?(?:comprar|provar|encontrar|degustar|comer|jantar|almo[cç]ar)|(?:posso|podemos)\\s+(?:comprar|provar|encontrar|degustar|comer|jantar|almo[cç]ar)|where\\s+(?:can\\s+i\\s+|to\\s+)(?:buy|try|taste|find|eat)|d[oó]nde\\s+(?:puedo\\s+)?(?:comprar|probar|encontrar|comer)|o[uù]\\s+(?:puis-je\\s+)?(?:acheter|go[uû]ter|trouver|manger)|dove\\s+(?:posso\\s+)?(?:comprare|assaggiare|trovare|mangiare)|wo\\s+(?:kann\\s+ich\\s+)?(?:kaufen|probieren|finden|essen)|recomenda\\w*|recomienda\\w*|recommend\\w*|sugere\\w*)\\b`,
  "iu",
);

/** Palavras de produto/categoria: nunca iniciam o nome de uma cidade. */
const PRODUCT_TOKENS = new Set([
  "doce", "doces", "docaria", "pastelaria", "pastelarias", "padaria", "padarias",
  "confeitaria", "confeitarias", "bolo", "bolos", "sobremesa", "sobremesas",
  "praia", "praias", "restaurante", "restaurantes", "museu", "museus", "bar", "bares",
  "hotel", "hoteis", "cafe", "cafes", "parque", "parques", "miradouro", "miradouros",
  "sweets", "sweet", "pastry", "bakery", "bakeries", "beach", "beaches",
  "restaurant", "restaurants", "museum", "museums", "hotels", "cafes",
]);

/**
 * "melhor restaurante DE francesinhas" — o que vem a seguir a um substantivo
 * de categoria é a especialidade pedida, nunca a cidade.
 */
const CATEGORY_NOUNS = new Set([
  ...PRODUCT_TOKENS,
  "tasca", "tascas", "loja", "lojas", "casa", "casas", "sitio", "sitios",
  "lugar", "lugares", "place", "places", "spot", "spots", "shop", "shops",
]);

/** Zonas amplas ("Norte de Portugal", "Sul de Espanha"): não são cidades. */
const REGION_LEAD_TOKENS = new Set([
  "norte", "sul", "centro", "leste", "oeste", "litoral", "interior", "nordeste",
  "noroeste", "sudeste", "sudoeste", "regiao", "zona", "north", "south", "east",
  "west", "region", "area", "coast", "costa",
]);

const DISCOVERY_AREA = new RegExp(
  `\\b(?:de|do|da|dos|das|em|no|na|nos|nas|in|at|of|à|a)\\s+(${NAME})`,
  "giu",
);

function extractDiscoveryDestination(text: string): string | undefined {
  const lead = DISCOVERY_LEAD.exec(text);
  DISCOVERY_LEAD.lastIndex = 0;
  if (!lead) return undefined;
  const tail = text.slice((lead.index ?? 0) + lead[0].length);
  DISCOVERY_AREA.lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = DISCOVERY_AREA.exec(tail))) {
    const raw = match[1];
    const firstToken = fold(raw.split(/[\s-]/u)[0] ?? "");
    if (PRODUCT_TOKENS.has(firstToken) || REGION_LEAD_TOKENS.has(firstToken)) continue;
    // Palavra imediatamente antes da preposição: "restaurante de francesinhas".
    const before = fold(
      tail
        .slice(0, match.index ?? 0)
        .trim()
        .split(/\s+/u)
        .pop()
        ?.replace(/[^\p{L}\p{N}]/gu, "") ?? "",
    );
    if (CATEGORY_NOUNS.has(before)) continue;
    const city = normalizeCityName(raw);
    if (city && !PRODUCT_TOKENS.has(fold(city)) && !REGION_LEAD_TOKENS.has(fold(city.split(" ")[0]))) {
      DISCOVERY_AREA.lastIndex = 0;
      return city;
    }
  }
  return undefined;
}


export function extractDestinationFromPrompt(prompt: string): ExtractedDestination {
  const text = (prompt ?? "").replace(/\s+/gu, " ").trim();
  if (!text) return { explicit: false };

  // "Estou em Sintra 1 dia…": a localidade escrita é o destino, mesmo quando
  // vem seguida de duração. Vem antes de qualquer padrão genérico
  // ("quero visitar alguns pontos turísticos") porque nomeia mesmo o lugar.
  const explicitLocality = extractExplicitLocality(text);
  const localityCity = explicitLocality ? normalizeCityName(explicitLocality) : undefined;
  if (localityCity) return { city: localityCity, explicit: true };

  // Pedido de recomendação: "melhor doce tradicional DE Viana do Castelo".
  const discoveryCity = extractDiscoveryDestination(text);
  if (discoveryCity) return { city: discoveryCity, explicit: true };




  // Melhor candidato = o que aparece primeiro no texto. Assim "Vou ao Porto…
  // vou de comboio a São Bento" mantém Porto como destino.
  let best: { index: number; city: string } | undefined;
  for (const pattern of DESTINATION_PATTERNS) {
    const match = pattern.exec(text);
    pattern.lastIndex = 0;
    const city = match?.[1] ? normalizeCityName(match[1]) : undefined;
    if (!city) continue;
    const index = match?.index ?? Number.MAX_SAFE_INTEGER;
    if (!best || index < best.index) best = { index, city };
  }

  const pair = ORIGIN_DESTINATION.exec(text);
  ORIGIN_DESTINATION.lastIndex = 0;
  if (pair) {
    const origin = normalizeCityName(pair[1]);
    const city = normalizeCityName(pair[2]);
    // Só é um par origem→destino quando a origem é mesmo um lugar
    // ("vou do Porto para Braga"), não um meio de transporte.
    if (origin && city && (pair.index ?? 0) <= (best?.index ?? Number.MAX_SAFE_INTEGER)) {
      return { city, origin, explicit: true };
    }
  }

  if (best) return { city: best.city, explicit: true };

  // "Porto, 6 horas, gastronomia" — forma compacta.
  const compact = text.match(new RegExp(`^\\s*(${NAME})\\s*[,;:]`, "u"))?.[1];
  const compactCity = compact ? normalizeCityName(compact) : undefined;
  if (compactCity) return { city: compactCity, explicit: true };

  // "Atenas 4 horas" / "atenas, 4 horas": forma compacta com duração,
  // válida mesmo quando o utilizador escreve o nome em minúsculas.
  const durationCompact = text.match(
    new RegExp(
      `^\\s*(${NAME}?)\\s*[,;:]?\\s+\\d{1,2}(?:[.,]\\d+)?\\s*(?:h|horas?|hours?|heures?|ore|stunden?|dias?|days?|jours?|giorni|tage)\\b`,
      "iu",
    ),
  )?.[1];
  const durationCompactCity = durationCompact ? normalizeCityName(durationCompact) : undefined;
  if (durationCompactCity) return { city: durationCompactCity, explicit: true };

  // Resposta curta à pergunta de esclarecimento: "Atenas". Sem esta regra,
  // a resposta era novamente considerada sem destino e a mesma pergunta
  // aparecia em ciclo. Mantém-se conservadora: apenas 1–4 palavras, sem
  // algarismos, e normalizeCityName rejeita termos genéricos como "cidade",
  // "viagem", "hotel" ou "museus".
  // Pontuação final ("Lagos!", "Londres?") não impede o reconhecimento.
  const bareText = text.replace(/[!?.,;:\s]+$/u, "");
  const bare = /^\s*[\p{L}][\p{L}'’.\-]*(?:\s+[\p{L}][\p{L}'’.\-]*){0,3}\s*$/u.test(bareText)
    ? normalizeCityName(bareText)
    : undefined;

  if (bare) return { city: bare, explicit: true };

  return { explicit: false };
}

/** Atalho: o pedido nomeia mesmo um destino? */
export function promptNamesDestination(prompt: string): boolean {
  return extractDestinationFromPrompt(prompt).explicit;
}

/**
 * Decide se devemos perguntar qual cidade usar: só quando o texto nomeia um
 * destino diferente do que está no campo manual escolhido pelo utilizador.
 */
export function destinationConflict(input: {
  promptCity?: string;
  fieldCity?: string;
  fieldWasUserSelected: boolean;
}): boolean {
  const { promptCity, fieldCity, fieldWasUserSelected } = input;
  if (!promptCity || !fieldCity || !fieldWasUserSelected) return false;
  return fold(promptCity) !== fold(fieldCity);
}
